import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styles from './ListingDetailPage.module.css';
import SEO, { buildListingSchema, buildBreadcrumbSchema } from '../components/SEO';
import ImageGallery from '../components/ImageGallery';

const API = '/api';

const FEATURE_LABELS = {
  balkon: '🏗 Balkon', taras: '☀️ Taras', garaz: '🚗 Garaż/Miejsce',
  winda: '🛗 Winda', ogrod: '🌿 Ogród', klimatyzacja: '❄️ Klimatyzacja',
  umeblowane: '🛋 Umeblowane', piwnica: '📦 Piwnica',
};

function formatPrice(price, unit) {
  if (price >= 1000000) return (price / 1000000).toFixed(2).replace('.', ',') + ' mln zł' + (unit ? ' ' + unit : '');
  return price.toLocaleString('pl-PL') + ' zł' + (unit ? ' ' + unit : '');
}

export default function ListingDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/listings/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Nie znaleziono ogłoszenia'); return r.json(); })
      .then(d => { setListing(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [slug]);

  if (loading) return <div className={styles.status}>Ładowanie ogłoszenia...</div>;
  if (error) return <div className={styles.statusErr}>{error} — <Link to="/ogloszenia">Wróć do listy</Link></div>;
  if (!listing) return null;

  const imgIndex = listing.title?.length % 3 || 0;
  const BADGE = { sprzedaz: 'Sprzedaż', wynajem: 'Wynajem', nowe: 'Nowe' };
  const mainImage = listing.images?.find(i => i.isMain)?.url || listing.images?.[0]?.url;
  const listingUrl = `https://nieruchomy.pl/ogloszenia/${listing.slug || listing.id}`;
  const metaDesc = listing.description
    ? listing.description.slice(0, 155).replace(/\n/g, ' ')
    : `${BADGE[listing.type]} — ${listing.area}m², ${listing.rooms} pok., ${listing.location}. ${Number(listing.price).toLocaleString('pl-PL')} zł${listing.priceUnit || ''}.`;

  return (
    <>
      <SEO
        title={`${listing.title} — ${listing.location}`}
        description={metaDesc}
        image={mainImage}
        url={listingUrl}
        schema={[
          buildListingSchema(listing),
          buildBreadcrumbSchema([
            { name: 'Strona główna', url: '/' },
            { name: 'Ogłoszenia', url: '/ogloszenia' },
            { name: listing.title, url: `/ogloszenia/${listing.slug || listing.id}` },
          ]),
        ]}
      />
      <div className={styles.page}>
        <button className={styles.backTop} onClick={() => navigate(-1)} aria-label="Wróć">← Wróć</button>
        <div className={styles.breadcrumb}>
          <Link to="/">Strona główna</Link> › <Link to="/ogloszenia">Ogłoszenia</Link> › <span>{listing.title}</span>
        </div>

        <div className={styles.layout}>
          {/* LEFT */}
          <div>
            <div className={styles.galleryWrap}>
              <ImageGallery images={listing.images || []} title={listing.title} index={imgIndex} />
              <div className={styles.galleryMeta}>
                <span className={`${styles.badge} ${styles['badge_' + listing.type]}`}>{BADGE[listing.type]}</span>
                <button className={`${styles.heartBtn} ${liked ? styles.liked : ''}`} onClick={() => setLiked(l => !l)}>
                  {liked ? '❤️' : '🤍'} {liked ? 'Zapisano' : 'Zapisz'}
                </button>
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.priceRow}>
                <span className={styles.price}>{formatPrice(listing.price, listing.priceUnit)}</span>
                <span className={styles.priceM2}>
                  {Math.round(listing.price / listing.area).toLocaleString('pl-PL')} zł/m²
                </span>
              </div>
              <h1 className={styles.title}>{listing.title}</h1>
              <p className={styles.address}>📍 {listing.address || listing.location}</p>
              <div className={styles.statsRow}>
                <div className={styles.statBox}><strong>{listing.area}</strong><span>m²</span></div>
                <div className={styles.statBox}><strong>{listing.rooms}</strong><span>pokoje</span></div>
                <div className={styles.statBox}><strong>{listing.floor}</strong><span>piętro</span></div>
                <div className={styles.statBox}><strong>{listing.year}</strong><span>rok budowy</span></div>
              </div>
            </div>

            {listing.description && (
              <div className={styles.card}>
                <h2>Opis nieruchomości</h2>
                <p className={styles.desc}>{listing.description}</p>
              </div>
            )}

            {listing.features?.length > 0 && (
              <div className={styles.card}>
                <h2>Udogodnienia</h2>
                <div className={styles.featureGrid}>
                  {listing.features.map(f => (
                    <span key={f} className={styles.featureTag}>{FEATURE_LABELS[f] || f}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT */}
          <div>
            <div className={`${styles.card} ${styles.agentCard}`}>
              <div className={styles.agentHeader}>
                <div className={styles.avatar}>{listing.agent?.avatar || 'AG'}</div>
                <div>
                  <p className={styles.agentName}>{listing.agent?.name}</p>
                  <p className={styles.agentRole}>Agent nieruchomości</p>
                </div>
              </div>
              <div className={styles.contactInfo}>
                {listing.agent?.phone && (
                  <a href={`tel:${listing.agent.phone}`} className={styles.contactRow}>
                    <span className={styles.contactIcon}>📞</span>
                    <div>
                      <span className={styles.contactLabel}>Telefon</span>
                      <span className={styles.contactValue}>{listing.agent.phone}</span>
                    </div>
                  </a>
                )}
                {listing.agent?.email && (
                  <a href={`mailto:${listing.agent.email}`} className={styles.contactRow}>
                    <span className={styles.contactIcon}>✉</span>
                    <div>
                      <span className={styles.contactLabel}>E-mail</span>
                      <span className={styles.contactValue}>{listing.agent.email}</span>
                    </div>
                  </a>
                )}
                {!listing.agent?.phone && !listing.agent?.email && (
                  <p className={styles.noContact}>Brak danych kontaktowych</p>
                )}
              </div>
            </div>

            <div className={styles.card} style={{ marginTop: '1.25rem' }}>
              <h3 style={{ marginBottom: '12px', fontSize: '15px', color: 'var(--mist)' }}>Szczegóły oferty</h3>
              {[
                ['Typ', BADGE[listing.type]],
                ['Powierzchnia', listing.area + ' m²'],
                ['Liczba pokoi', listing.rooms],
                ['Piętro', listing.floor],
                ['Rok budowy', listing.year],
                ['Lokalizacja', listing.location],
              ].map(([k, v]) => (
                <div key={k} className={styles.detailRow}>
                  <span>{k}</span><span>{v}</span>
                </div>
              ))}
            </div>

            <button className={styles.backBtn} onClick={() => navigate(-1)}>← Wróć do listy</button>
          </div>
        </div>
      </div>
    </>
  );
}
